import { ArrayParam, StringParam, useQueryParams, withDefault } from 'use-query-params'
import { useQueries } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { useQueryList } from '@percent/utility'
import { Alert } from '@percent/lemonade'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { BankDetailsTable } from '@percent/admin-dashboard/app/bankDetails/bankDetailsContainer/bankDetailsTable/BankDetailsTable'

const MERGE_MODE_PAGE_SIZE = 100

export function BankDetailsContainer() {
  const { bankDetailsService } = useServices()
  const { t } = useTranslation()

  const [queryParams, setQueryParams] = useQueryParams({
    statusFilter: withDefault(ArrayParam, ['pending']),
    direction: withDefault(StringParam, 'DESC'),
    organisationIds: withDefault(ArrayParam, []),
    startDate: withDefault(StringParam, undefined),
    endDate: withDefault(StringParam, undefined)
  })

  const statusFilter = (queryParams.statusFilter ?? ['pending']).filter((s): s is string => s != null) as string[]
  const organisationIds = (queryParams.organisationIds ?? []).filter((id): id is string => id != null) as string[]

  const isMergeMode = organisationIds.length > 0 && statusFilter.length > 1

  const [{ data, isLoading, isFetching, totalResults, errorMessage }, { nextPage, previousPage }] = useQueryList(
    ['v1/admin/bank-details-reviews', statusFilter, queryParams.direction, queryParams.startDate, queryParams.endDate],
    bankDetailsService.getBankDetailsList,
    {
      ...queryParams,
      statusFilter,
      organisationIds,
      startDate: queryParams.startDate,
      endDate: queryParams.endDate
    },
    { enabled: !isMergeMode }
  )

  const mergeQueries = useQueries({
    queries: isMergeMode
      ? statusFilter.map(status => ({
          queryKey: [
            'v1/admin/bank-details-reviews',
            'merge',
            status,
            organisationIds,
            queryParams.direction,
            queryParams.startDate,
            queryParams.endDate
          ],
          queryFn: () =>
            bankDetailsService.getBankDetailsList({
              statusFilter: [status],
              organisationIds,
              startDate: queryParams.startDate,
              endDate: queryParams.endDate,
              pageSize: MERGE_MODE_PAGE_SIZE
            })
        }))
      : []
  })

  if (isMergeMode) {
    const directionMultiplier = queryParams.direction === 'ASC' ? 1 : -1
    const mergedRows = mergeQueries
      .flatMap(query => query.data?.data.data ?? [])
      .sort((a, b) => a.requestDate.localeCompare(b.requestDate) * directionMultiplier)
    const mergedTotal = mergeQueries.reduce((sum, query) => sum + (query.data?.data.totalResults ?? 0), 0)
    const mergedLoading = mergeQueries.some(query => query.isLoading || query.isFetching)
    const mergedError = mergeQueries.some(query => query.isError) ? 'error' : null
    const isTruncated = mergedRows.length < mergedTotal

    return (
      <>
        {isTruncated && (
          <Alert variant="warning" title={t('bankDetails.mergeMode.truncatedTitle')}>
            {t('bankDetails.mergeMode.truncatedDescription', { shown: mergedRows.length, total: mergedTotal })}
          </Alert>
        )}
        <BankDetailsTable
          data={mergedRows}
          isLoading={mergedLoading}
          errorMessage={mergedError}
          nextPage={null}
          previousPage={null}
          totalResults={mergedTotal}
          queryParams={queryParams}
          setQueryParams={setQueryParams}
          hidePagination
        />
      </>
    )
  }

  return (
    <BankDetailsTable
      data={data}
      isLoading={isLoading || isFetching}
      errorMessage={errorMessage}
      nextPage={nextPage}
      previousPage={previousPage}
      totalResults={totalResults ?? 0}
      queryParams={queryParams}
      setQueryParams={setQueryParams}
    />
  )
}
